<template>
    <div class="users__container">
        <div class="users__wrapper">
            <ReportingFilters />
            <div class="users__tags-and-buttons">
                <Tags class="users__tags" :tags="filterTags" />
                <div class="users__buttons">
                    <TotemButton v-if="isDesktopView" @click="toggleCharts" class="users__toggle-charts-button" icon="pie-chart1" :label="hideButtonTextLabel" />
                    <TotemButton
                        :disabled="isLoadingExcel"
                        color="primary"
                        size="medium"
                        class="users__download-excel-button"
                        :icon="getIconForExcelExport"
                        @click="downloadExcel('Users')"
                        :label="$t('OrganizationReport.export_as_excel_file')"
                    />
                </div>
            </div>
            <ReportingCharts :chartsSummary="chartsSummary" v-show="chartsVisible">
               <TotalHoursWatchedChart :graphData="totalHoursWatched" />
               <UserActivityChart :graphData="mostActiveUsers" :key="chartKey" />
            </ReportingCharts>
        </div>
        <UsersTable :data="members" :key="tableKey" />
    </div>
</template>

<script>
    import { mapGetters } from 'vuex'
    import ReportingFilters from 'components/user/organizationreport/filters/ReportingFilters'
    import loadingOverlay from 'components/shared/mixins/loadingOverlay'
    import ReportingCharts from 'components/user/organizationreport/charts/ReportingCharts'
    import UsersTable from 'components/user/organizationreport/tabs/users/tables/UsersTable'
    import TotalHoursWatchedChart from 'components/user/organizationreport/tabs/users/charts/TotalHoursWatchedChart'
    import UserActivityChart from 'components/user/organizationreport/tabs/users/charts/UserActivityChart'
    import convertMinutesToHours from 'components/shared/mixins/convertMinutesToHours'
    import Tags from 'components/user/organizationreport/filters/Tags'
    import excelHelper from 'components/user/organizationreport/excel/helpers/excelHelper'

    export default {
        components: {
            ReportingFilters,
            ReportingCharts,
            UsersTable,
            TotalHoursWatchedChart,
            UserActivityChart,
            Tags
        },
        mixins: [loadingOverlay, convertMinutesToHours, excelHelper],
        data () {
            return {
                itemsReady: false,
                chartsVisible: true,
                tableKey: 0,
                chartKey: 0
            }
        },
        provide () {
            return {
                tab: 'Users'
            }
        },
        computed: {
            ...mapGetters({
                reportingItems: 'User/getReportingItems',
                isLoggedIn: 'isLoggedIn',
                members: 'ReportsUsers/searchResults',
                dashboard: 'ReportsUsers/getDashboard',
                totalHoursWatched: 'ReportsUsers/getTotalHoursWatched',
                mostActiveUsers: 'ReportsUsers/getMostActiveUsers',
                tags: 'ReportsUsers/tags',
                filterStaticData: 'ReportsUsers/getFilterStaticData',
                selectedCountryCode: 'Languages/getSelectedCountryCode'
            }),
            chartsSummary () {
                return [
                    {
                        title: this.$t('OrganizationReport.active_users'),
                        amount: this.dashboard?.activeUsers,
                        tooltip: this.$t('OrganizationReport.active_users_tooltip')
                    },
                    {
                        title: this.$t('OrganizationReport.average_watched_time'),
                        amount: this.roundedAverageWatchTime(),
                        tooltip: this.$t('OrganizationReport.average_watched_time_tooltip')
                    },
                    {
                        title: this.$t('OrganizationReport.total_watched_time'),
                        amount: this.totalWatchTime(),
                        tooltip: this.$t('OrganizationReport.total_watched_time_tooltip')
                    }
                ]
            },
            filterTags () {
                return this.tags.map(tag => ({
                    ...tag,
                    translatedName: this.getTranslationForTagName(tag),
                    translatedLabel: this.$t(this.getTranslationForTagLabel(tag))
                }))
            },
            hideButtonTextLabel () {
                return this.chartsVisible ? this.$t('OrganizationReport.hide_graph') : this.$t('OrganizationReport.show_graph')
            }
        },
        methods: {
            toggleCharts () {
                this.chartsVisible = !this.chartsVisible
            },
            getTranslationForTagName (tag) {
                if (!this.filterStaticData[tag.filterKey]['labels']) {
                    return tag.name
                }
                return this.filterStaticData[tag.filterKey]['labels'][tag.name]
            },
            getTranslationForTagLabel (tag) {
                return this.filterStaticData[tag.filterKey]['translationString']
            },
            roundedAverageWatchTime () {
                if (Object.keys(this.dashboard).length !== 0) {
                    let averageWatchTime = Math.round(this.dashboard?.averageWatchTime)
                    return this.convertMinutesToHours(averageWatchTime)
                }
            },
            totalWatchTime () {
                if (Object.keys(this.dashboard).length !== 0) {
                    let totalWatchTime = Math.round(this.dashboard?.totalWatchTime)
                    return this.convertMinutesToHours(totalWatchTime)
                }
            }
        },
        async created () {
            await this.$store.dispatch('ReportsUsers/enableSearch')
            await this.$store.dispatch('ReportsUsers/initSearch')
            this.chartKey += 1
        },
        watch: {
            selectedCountryCode: {
                handler () {
                    this.tableKey += 1
                    this.$store.dispatch('ReportsUsers/initSearch')
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
@include organization-reporting-excel-export;

.users {
    &__container {
        display: flex;
        flex-direction: column;
        font-family: Nunito-Regular, sans-serif;

        [class*="desktop"] & {
            flex-direction: column;
        }
    }

    &__wrapper {
        border: 1px solid $gallery;
    }

    &__tags-and-buttons {
        display: flex;
        justify-content: space-between;
        gap: 10px;
        align-items: flex-start;
        background: $ghost_white;
        padding: 0 20px 20px;
    }

    &__tags {
        position: relative;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
    }

    &__buttons {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        justify-content: flex-end;
    }

    &__download-excel-button,
    &__toggle-charts-button {
        white-space: nowrap;
    }
}
</style>
